/**
 * useAllSettings Hook
 *
 * Loads and provides both general and application settings.
 * Combines general settings (language, timezone, theme, etc.) with
 * application settings (pagination, EEG defaults, etc.)
 *
 * @returns {object} { settings, loading, error, refetch }
 */

import { useState, useEffect, useCallback } from "react";
import {
  settingsService,
  type GeneralSettings,
  type ApplicationSettings as AppSettings,
} from "@features/Settings/services/SettingsService";
import { useAuth } from "@shared/context/AuthContext";
import logger from "@shared/utils/logger";

export interface AllSettings extends AppSettings {
  // General settings
  language: string;
  timezone: string;
  date_format: string;
  time_format: string;
  theme: string;
  font_size: string;
  color_vision_mode: string;
  default_view: string;
  high_contrast: boolean;
  reduce_motion: boolean;
  screen_reader: boolean;
  workspace_density: "comfortable" | "cozy" | "compact";
}

interface UseAllSettingsReturn {
  settings: AllSettings;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  updateGeneralSettings: (
    patch: Partial<GeneralSettings>,
  ) => Promise<GeneralSettings>;
  updateApplicationSettings: (
    patch: Partial<AppSettings>,
  ) => Promise<AppSettings>;
}

/**
 * Default settings used as fallback
 */
export const DEFAULT_SETTINGS: AllSettings = {
  // General settings defaults
  language: "en",
  timezone: "UTC",
  date_format: "MM/DD/YYYY",
  time_format: "12h",
  theme: "light",
  font_size: "medium",
  color_vision_mode: "none",
  default_view: "dashboard",
  high_contrast: false,
  reduce_motion: false,
  screen_reader: false,
  workspace_density: "cozy",
  // Application settings defaults
  auto_save: true,
  auto_save_interval: 60,
  confirm_navigation: true,
  items_per_page: 25,
  default_sort: "asc",
  default_montage_id: null,
  default_sensitivity: 7.0,
  default_time_scale: 10,
  custom_preferences: null,
};

/**
 * Hook to load and manage all user settings (general + application)
 */
export const useAllSettings = (): UseAllSettingsReturn => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [settings, setSettings] = useState<AllSettings>(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadSettings = useCallback(async () => {
    const path = typeof window !== "undefined" ? window.location.pathname : "";
    const isAuthRoute =
      path.startsWith("/login") ||
      path.startsWith("/forgot-password") ||
      path.startsWith("/reset-password") ||
      path.startsWith("/accept-invitation");
    if (authLoading) {
      setLoading(true);
      return;
    }

    if (!isAuthenticated || isAuthRoute) {
      setLoading(false);
      setError(null);
      setSettings(DEFAULT_SETTINGS);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Load both general and application settings in parallel
      const [generalSettings, appSettings] = await Promise.all([
        settingsService.getGeneralSettings(),
        settingsService.getApplicationSettings(),
      ]);

      // Merge both settings objects
      const customPreferences =
        appSettings.custom_preferences &&
        typeof appSettings.custom_preferences === "object"
          ? appSettings.custom_preferences
          : {};
      const workspaceDensity =
        customPreferences.workspace_density === "comfortable" ||
        customPreferences.workspace_density === "compact" ||
        customPreferences.workspace_density === "cozy"
          ? customPreferences.workspace_density
          : DEFAULT_SETTINGS.workspace_density;

      setSettings({
        ...DEFAULT_SETTINGS,
        ...generalSettings,
        ...appSettings,
        workspace_density: workspaceDensity,
      });
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to load settings";
      logger.warn("Failed to load settings, using defaults:", errorMessage);
      setError(errorMessage);
      // Keep using DEFAULT_SETTINGS on error
    } finally {
      setLoading(false);
    }
  }, [authLoading, isAuthenticated]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const refetch = useCallback(async () => {
    await loadSettings();
  }, [loadSettings]);

  const updateGeneralSettings = useCallback(
    async (patch: Partial<GeneralSettings>): Promise<GeneralSettings> => {
      const updated = await settingsService.updateGeneralSettings(patch);
      setSettings((prev) => ({
        ...prev,
        ...updated,
      }));
      return updated;
    },
    [],
  );

  const updateApplicationSettings = useCallback(
    async (patch: Partial<AppSettings>): Promise<AppSettings> => {
      const updated = await settingsService.updateApplicationSettings(patch);
      const customPreferences =
        updated.custom_preferences &&
        typeof updated.custom_preferences === "object"
          ? updated.custom_preferences
          : {};
      const workspaceDensity =
        customPreferences.workspace_density === "comfortable" ||
        customPreferences.workspace_density === "compact" ||
        customPreferences.workspace_density === "cozy"
          ? customPreferences.workspace_density
          : DEFAULT_SETTINGS.workspace_density;

      setSettings((prev) => ({
        ...prev,
        ...updated,
        workspace_density: workspaceDensity,
      }));
      return updated;
    },
    [],
  );

  return {
    settings,
    loading,
    error,
    refetch,
    updateGeneralSettings,
    updateApplicationSettings,
  };
};

export default useAllSettings;
